import {
  IPaginationMetadata,
  ORDER_TYPE,
  removeCpfMask,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllPersonProfileUseCaseInterface,
  GetAllPersonUseCaseInterface,
  GetByIdPersonUseCaseInterface,
} from 'domain/usecases';
import { PERSON_ORDER_COLUMNS } from 'domain/value-objects/person-order-columns';
import {
  getPtBrPluralNameFromProfileType,
  PROFILE_TYPE,
} from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useInfiniteScroll } from 'presentation/app/hooks/use-infinite-scroll';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  loadPersonsListData,
  changePersonsListPaginationMetadata,
  changePersonsListFilters,
  clearPersonsListPaginationMetadata,
  clearPersonsListData,
} from 'presentation/config/store/person/person-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

type DataViewMode = 'grid' | 'list';

const profileTypeOptions = Object.values(PROFILE_TYPE)
  .filter((value): value is PROFILE_TYPE => typeof value === 'number')
  .map((value) => ({
    value,
    label: getPtBrPluralNameFromProfileType(value).replace(/^\w/, (c) =>
      c.toUpperCase(),
    ),
  }));

const requestsStatusInitialState = {
  getAllPersons: STATUSPAGE.INITIAL,
  getOffcanvasData: STATUSPAGE.INITIAL,
};

type RequestsStatus = typeof requestsStatusInitialState;

interface PersonDetailsOffcanvasData {
  person: GetByIdPersonUseCaseInterface.Result | undefined;
  profiles: GetAllPersonProfileUseCaseInterface.Result;
}

export function usePersonsList() {
  // Hooks
  const dispatch = useDispatch();
  const { personService } = useService();
  const {
    personsListData,
    personsListFilters,
    personsListHasNextPage,
    personsListPaginationMetadata,
  } = useAppSelector((state) => state.Person);
  const [requestsStatus, setRequestsStatus] = useState<RequestsStatus>(
    requestsStatusInitialState,
  );

  // States
  const [dataViewMode, setDataViewMode] = useState<DataViewMode>(
    (localStorage.getItem('preferedDataViewMode') as DataViewMode | null) ||
      'grid',
  );
  const [isPersonDetailsOffcanvasOpen, setIsPersonDetailsOffcanvasOpen] =
    useState(false);
  const [personDetailsOffcanvasData, setPersonDetailsOffcanvasData] =
    useState<PersonDetailsOffcanvasData>({
      person: undefined,
      profiles: [],
    });

  // Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: personsListFilters.name || '',
      cpf: personsListFilters.cpf || '',
      email: personsListFilters.email || '',
      profileType:
        profileTypeOptions.find(
          (option) => option.value === personsListFilters.profileType,
        ) || null,
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().min(3, 'O nome deve ter pelo menos 3 caracteres'),
      cpf: Yup.string().min(14, 'O CPF deve ter pelo menos 11 caracteres'),
      email: Yup.string().min(3, 'O email deve ter pelo menos 3 caracteres'),
      profileType: Yup.object()
        .shape({
          value: Yup.number()
            .oneOf(profileTypeOptions.map((option) => option.value))
            .required(),
          label: Yup.string().required(),
        })
        .nullable(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        page: 0,
        length: 12,
        orderColumn: PERSON_ORDER_COLUMNS.NAME,
        orderDirection: ORDER_TYPE.ASC,
        cpfQuery: removeCpfMask(values.cpf),
        nameQuery: values.name,
        emailQuery: values.email,
        profileType: values.profileType
          ? [values.profileType.value]
          : undefined,
      };

      dispatch(clearPersonsListPaginationMetadata());
      dispatch(clearPersonsListData());
      dispatch(
        changePersonsListFilters({
          cpf: values.cpf,
          name: values.name,
          email: values.email,
          profileType: values.profileType?.value || null,
        }),
      );
      requestGetAllPersons(valuesToSubmit);
    },
  });

  // Methods
  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<PERSON_ORDER_COLUMNS>>(
      key: T,
      value: IPaginationMetadata<PERSON_ORDER_COLUMNS>[T],
    ) => {
      dispatch(changePersonsListPaginationMetadata({ [key]: value }));
    },
    [personsListPaginationMetadata],
  );

  const changeRequestsStatus = useCallback(
    <T extends keyof RequestsStatus>(key: T, value: RequestsStatus[T]) => {
      setRequestsStatus((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const requestGetAllPersons = useCallback(
    async (params: GetAllPersonUseCaseInterface.Params) => {
      changeRequestsStatus('getAllPersons', STATUSPAGE.LOADING);
      personService
        .getAllPerson(params)
        .then((res) => {
          dispatch(loadPersonsListData(res));

          if (res.data.length === 0) {
            throw new Error('Não foram encontrados registros.');
          }
        })
        .catch((err) => {
          notifyError(err?.message || 'Algo de errado aconteceu.');
          dispatch(clearPersonsListData());
        })
        .finally(() => {
          changeRequestsStatus('getAllPersons', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const getPersonsFromNextPage = useCallback(() => {
    const nextPage = personsListPaginationMetadata.selectedPage + 1;
    const valuesToSubmit = {
      page: nextPage,
      length: 12,
      orderDirection: ORDER_TYPE.ASC,
      orderColumn: PERSON_ORDER_COLUMNS.NAME,
      cpfQuery: removeCpfMask(personsListFilters.cpf),
      nameQuery: personsListFilters.name,
      emailQuery: personsListFilters.email,
      profileType: personsListFilters.profileType
        ? [personsListFilters.profileType]
        : undefined,
    };

    requestGetAllPersons(valuesToSubmit).then(() => {
      changePaginationMetadata('selectedPage', nextPage);
    });
  }, [personsListPaginationMetadata.selectedPage, personsListFilters]);

  const requestGetOffcanvasData = useCallback(async (id: string) => {
    const getPersonByIdPromise = personService.getByIdPerson({ id });
    const getAllProfilesFromPersonPromise = personService.getAllPersonProfile({
      id,
    });

    changeRequestsStatus('getOffcanvasData', STATUSPAGE.LOADING);

    Promise.all([getPersonByIdPromise, getAllProfilesFromPersonPromise])
      .then(([getPersonByIdPromiseRes, getAllProfilesFromPersonPromiseRes]) => {
        setPersonDetailsOffcanvasData((prevState) => {
          return {
            ...prevState,
            person: getPersonByIdPromiseRes,
            profiles: getAllProfilesFromPersonPromiseRes,
          };
        });
      })
      .catch((err) => {
        togglePersonDetailsOffcanvas();
        notifyError(err?.message || 'Algo de errado aconteceu.');
      })
      .finally(() => {
        changeRequestsStatus('getOffcanvasData', STATUSPAGE.DONE);
      });
  }, []);

  const clearPersonDetailsOffcanvasData = useCallback(() => {
    setPersonDetailsOffcanvasData((prevState) => {
      return {
        ...prevState,
        person: undefined,
        profiles: [],
      };
    });
  }, []);

  const changeDataViewMode = useCallback((dataViewMode: DataViewMode) => {
    setDataViewMode(dataViewMode);
    localStorage.setItem('preferedDataViewMode', dataViewMode);
  }, []);

  const togglePersonDetailsOffcanvas = useCallback(
    () => setIsPersonDetailsOffcanvasOpen((prevState) => !prevState),
    [],
  );

  const { lastElementCallbackRef: lastPersonListGridCardCallbackRef } =
    useInfiniteScroll({
      isLoading: requestsStatus.getAllPersons === STATUSPAGE.LOADING,
      hasMoreData: personsListHasNextPage,
      fetchNextChunkOfDataCallbackFn: getPersonsFromNextPage,
    });

  return {
    changeDataViewMode,
    requestGetOffcanvasData,
    togglePersonDetailsOffcanvas,
    clearPersonDetailsOffcanvasData,
    lastPersonListGridCardCallbackRef,
    validation,
    dataViewMode,
    requestsStatus,
    personsListData,
    personsListHasNextPage,
    personDetailsOffcanvasData,
    isPersonDetailsOffcanvasOpen,
    personsListPaginationMetadata,
    profileTypeOptions,
  };
}
